import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createBlock as _createBlock, createVNode as _createVNode, withModifiers as _withModifiers, resolveDirective as _resolveDirective, withDirectives as _withDirectives, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = { class: "context-menu-bar-translation-manager" }
const _hoisted_2 = { class: "dd-item-container" }
const _hoisted_3 = { class: "dd-item-icon" }
const _hoisted_4 = {
  class: "dd-item-text",
  "data-cy": "context-menu-view"
}

import DeepLSplitButton from "@common/components/DeepLSplitButton.vue";
import Icon from "@common/components/Icon.vue";
import MenuBar from "@common/components/MenuBar/MenuBar.vue";
import ProgressModal from "@common/components/ProgressModal.vue";
import { NavMenuItem, ShortcutDefinition } from "@medapro-types/common/common";
import { ProgressHandler } from "@medapro-types/common/components/ProgressHandling";
import { CreateTranslationProjectData, TranslationProject } from "@medapro-types/TranslationManager/translationManager";
import translationManagerApi from "@services/translationManager";
import CreateTranslationProjectModal from "@TranslationManager/components/CreateTranslationProjectModal.vue";
import RenameTranslationProjectModal from "@TranslationManager/components/RenameTranslationProjectModal.vue";
import {
  useTranslationManagerActions,
  useTranslationManagerGetters,
  useTranslationManagerState
} from "@TranslationManager/composables/useTranslationManagerStore";
import { naturalSort } from "@util/naturalSort";
import emitter from "tiny-emitter/instance";
import { computed, DeepReadonly, onMounted, onUnmounted, reactive } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import ExportTranslationsButton from "./ExportButton.vue";
import ImportTranslationsButton from "./ImportButton.vue";
import OpenTranslationProjectModal from "./OpenTranslationProjectModal.vue";
import ViewOptionsModal from "./OptionsMenu/ViewOptionsModal.vue";

//#region General
type ViewModel = {
  showRenameModal: boolean;
  showCreateModal: boolean;
  showOpenModal: boolean;
  exportMenuItems: Array<NavMenuItem>;
  progressHandler: ProgressHandler;
  shortcuts: ShortcutDefinition[];
};


export default /*#__PURE__*/_defineComponent({
  __name: 'ContextMenuBar',
  setup(__props) {

const { t: $t } = useI18n();

const router = useRouter();
//#endregion

//#region Store
const { translationProjects, currentTranslationProject } = useTranslationManagerState();
const {
  renameTranslationProject,
  deleteTranslationProject: deleteProject,
  createTranslationProject,
  translateEntriesWithDeepL,
  publishTranslationProject: publishProject
} = useTranslationManagerActions();
const { hasUnsavedData } = useTranslationManagerGetters();
//#endregion

//#region ViewModel
const vm = reactive<ViewModel>({
  showRenameModal: false,
  showCreateModal: false,
  showOpenModal: false,
  exportMenuItems: [
    {
      text: $t("translationManager.exportExcel"),
      icon: "mdp-FileExcel"
    },
    {
      text: $t("translationManager.importXliff"),
      icon: "mdp-Export"
    }
  ],
  progressHandler: ProgressHandler.createIndeterminate({ id: "translation-project-progress-modal", isCancellable: true }),
  shortcuts: [
    {
      key: "n",
      ctrlKey: true,
      altKey: true,
      callback: () => openCreateTranslationProjectDialog()
    },
    {
      key: "o",
      ctrlKey: true,
      altKey: true,
      callback: () => openTranslationProjectManageDialog()
    },
    {
      ctrlKey: true,
      key: "delete",
      callback: () => deleteTranslationProject()
    },
    {
      key: "F2",
      callback: () => beginRenameTranslationProject()
    }
  ]
});
//#endregion

//#region Saving
const checkDataChangeAllowed = () => {
  return !hasUnsavedData.value || confirm($t("confirmNavigation"));
};
//#endregion

//#region Project selection
const translationProjectNames = computed(() => {
  return translationProjects.value.map((project) => project.name);
});

const canSelectTranslationProject = computed(() => {
  return translationProjects.value.length > 0;
});

const sortedTranslationProjects = computed(() => {
  const sorted = naturalSort(translationProjects.value, "name");
  return sorted;
});

const switchTranslationProject = async (project: DeepReadonly<TranslationProject>) => {
  if (project.translationProjectId === currentTranslationProject.value?.translationProjectId) {
    return;
  }

  router.push({
    name: "translation-manager",
    params: {
      translationProjectId: project.translationProjectId
    }
  });
};

const openTranslationProjectManageDialog = () => {
  if (!vm.showOpenModal) {
    vm.showOpenModal = true;
  }
};
//#endregion

//#region Project creation
const openCreateTranslationProjectDialog = () => {
  if (!vm.showCreateModal) {
    vm.showCreateModal = true;
  }
};

const confirmCreateTranslationProject = async (creationData: CreateTranslationProjectData) => {
  if (!checkDataChangeAllowed()) {
    return;
  }

  const createdProject = await createTranslationProject(creationData);
  if (createdProject) {
    await switchTranslationProject(createdProject);
  }
};
//#endregion

//#region Project rename
const canRename = computed(() => {
  return currentTranslationProject.value != null;
});

const beginRenameTranslationProject = () => {
  if (!vm.showRenameModal && currentTranslationProject.value) {
    vm.showRenameModal = true;
  }
};

const confirmRename = async (newName: string) => {
  await renameTranslationProject({ translationProjectId: currentTranslationProject.value?.translationProjectId, name: newName });
};

const cancelRename = () => {};
//#endregion

//#region Project delete
const canDelete = computed(() => {
  return currentTranslationProject.value != null;
});

const deleteTranslationProject = async () => {
  if (!currentTranslationProject.value) {
    return;
  }
  if (!confirm($t("translationManager.deleteTranslationProjectQuestion", { projectName: currentTranslationProject.value.name }))) {
    return;
  }

  await deleteProject(currentTranslationProject.value);
};
//#endregion

//#region Publish
const canPublish = computed(() => {
  return currentTranslationProject.value != null;
});

const publishTranslationProject = async () => {
  if (!currentTranslationProject.value || !checkDataChangeAllowed()) {
    return;
  }

  const hasOverlongValues = await translationManagerApi.hasOverlongTargetValues(currentTranslationProject.value.translationProjectId);
  if (
    (hasOverlongValues && !confirm($t("translationManager.askPublishProjectWithOverlongValues"))) ||
    (!hasOverlongValues && !confirm($t("translationManager.askPublishProject")))
  ) {
    return;
  }

  await publishProject({ progressHandler: vm.progressHandler });
};

const publishCancelled = () => {
  vm.progressHandler.cancel(`${$t("translationManager.cancellingPublish")}...`);
};
//#endregion

//#region DeepL translation
const canTranslateByAI = computed(() => {
  return currentTranslationProject.value != null;
});

const translate = async (onlyEmpty: boolean) => {
  if (!checkDataChangeAllowed()) {
    return;
  }

  await translateEntriesWithDeepL({
    translationProjectId: currentTranslationProject.value?.translationProjectId,
    onlyEmpty: onlyEmpty
  });
};

//#endregion

//#region Lifecycle
onMounted(() => {
  emitter.on("create-TM-project", openCreateTranslationProjectDialog);
  emitter.on("delete-TM-project", deleteTranslationProject);
  emitter.on("open-TM-project", openTranslationProjectManageDialog);
  emitter.on("rename-TM-project", beginRenameTranslationProject);
});

onUnmounted(() => {
  emitter.off("create-TM-project", openCreateTranslationProjectDialog);
  emitter.off("delete-TM-project", deleteTranslationProject);
  emitter.off("open-TM-project", openTranslationProjectManageDialog);
  emitter.off("rename-TM-project", beginRenameTranslationProject);
});
//#endregion

return (_ctx: any,_cache: any) => {
  const _component_b_dropdown_item = _resolveComponent("b-dropdown-item")!
  const _component_b_dropdown = _resolveComponent("b-dropdown")!
  const _component_b_btn = _resolveComponent("b-btn")!
  const _directive_tooltip = _resolveDirective("tooltip")!
  const _directive_b_modal = _resolveDirective("b-modal")!
  const _directive_shortcut = _resolveDirective("shortcut")!

  return _withDirectives((_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(MenuBar, null, {
      leftItems: _withCtx(() => [
        _createVNode(_component_b_dropdown, {
          "toggle-class": "mono",
          class: "dropdown-default",
          disabled: !canSelectTranslationProject.value
        }, {
          "button-content": _withCtx(() => [
            _createElementVNode("span", null, _toDisplayString(_unref(currentTranslationProject)?.name), 1)
          ]),
          default: _withCtx(() => [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(sortedTranslationProjects.value, (project) => {
              return (_openBlock(), _createBlock(_component_b_dropdown_item, {
                active: _unref(currentTranslationProject)?.translationProjectId == project.translationProjectId,
                onClick: ($event: any) => (switchTranslationProject(project)),
                key: project.translationProjectId
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(project.name), 1)
                ]),
                _: 2
              }, 1032, ["active", "onClick"]))
            }), 128))
          ]),
          _: 1
        }, 8, ["disabled"]),
        _withDirectives((_openBlock(), _createElementBlock("span", null, [
          _createVNode(_component_b_btn, {
            class: "btn-icon mono",
            onClick: _withModifiers(openCreateTranslationProjectDialog, ["prevent"])
          }, {
            default: _withCtx(() => [
              _createVNode(Icon, { icon: "Plus" })
            ]),
            _: 1
          }, 8, ["onClick"])
        ])), [
          [_directive_tooltip, _unref($t)('translationManager.newTranslationProject')]
        ]),
        _withDirectives((_openBlock(), _createElementBlock("span", null, [
          _createVNode(_component_b_btn, {
            class: "btn-icon mono",
            disabled: !canSelectTranslationProject.value,
            onClick: _withModifiers(openTranslationProjectManageDialog, ["prevent"])
          }, {
            default: _withCtx(() => [
              _createVNode(Icon, { icon: "Open" })
            ]),
            _: 1
          }, 8, ["disabled", "onClick"])
        ])), [
          [_directive_tooltip, _unref($t)('translationManager.openTranslationProject')]
        ]),
        _withDirectives((_openBlock(), _createElementBlock("span", null, [
          _createVNode(_component_b_btn, {
            class: "btn-icon mono",
            disabled: !canDelete.value,
            onClick: _withModifiers(deleteTranslationProject, ["prevent"])
          }, {
            default: _withCtx(() => [
              _createVNode(Icon, { icon: "Delete" })
            ]),
            _: 1
          }, 8, ["disabled", "onClick"])
        ])), [
          [_directive_tooltip, _unref($t)('translationManager.deleteTranslationProject')]
        ]),
        _withDirectives((_openBlock(), _createElementBlock("span", null, [
          _createVNode(_component_b_btn, {
            class: "btn-icon mono",
            disabled: !canRename.value,
            onClick: _withModifiers(beginRenameTranslationProject, ["prevent"])
          }, {
            default: _withCtx(() => [
              _createVNode(Icon, { icon: "Rename" })
            ]),
            _: 1
          }, 8, ["disabled", "onClick"])
        ])), [
          [_directive_tooltip, _unref($t)('translationManager.renameTranslationProject')]
        ]),
        _createVNode(ExportTranslationsButton, {
          id: "translation-project-export",
          "translation-project-id": _unref(currentTranslationProject)?.translationProjectId,
          "data-change-allowed-check": checkDataChangeAllowed,
          "translation-project-name": _unref(currentTranslationProject)?.name
        }, null, 8, ["translation-project-id", "translation-project-name"]),
        _createVNode(ImportTranslationsButton, {
          id: "translation-project-import",
          "translation-project-id": _unref(currentTranslationProject)?.translationProjectId,
          "data-change-allowed-check": checkDataChangeAllowed
        }, null, 8, ["translation-project-id"]),
        _createVNode(DeepLSplitButton, {
          onTranslateAll: _cache[0] || (_cache[0] = ($event: any) => (translate(false))),
          onTranslateEmpty: _cache[1] || (_cache[1] = ($event: any) => (translate(true))),
          "glossary-modal-name": "glossary-modal-translation-manager-menubar",
          "icon-only": true,
          disabled: !canTranslateByAI.value
        }, null, 8, ["disabled"]),
        _withDirectives((_openBlock(), _createElementBlock("span", null, [
          _createVNode(_component_b_btn, {
            class: "btn-icon mono",
            disabled: !canPublish.value,
            onClick: _withModifiers(publishTranslationProject, ["prevent"])
          }, {
            default: _withCtx(() => [
              _createVNode(Icon, { icon: "Publish" })
            ]),
            _: 1
          }, 8, ["disabled", "onClick"])
        ])), [
          [_directive_tooltip, _unref($t)('translationManager.finalizeTranslationProject')]
        ])
      ]),
      rightItems: _withCtx(() => [
        _createVNode(_component_b_dropdown, {
          "toggle-class": "btn-icon mono",
          right: "",
          "no-caret": "",
          "data-cy": "context-menu"
        }, {
          "button-content": _withCtx(() => [
            _createVNode(Icon, { icon: "Options" })
          ]),
          default: _withCtx(() => [
            _withDirectives((_openBlock(), _createBlock(_component_b_dropdown_item, { "link-class": "icon-item" }, {
              default: _withCtx(() => [
                _createElementVNode("div", _hoisted_2, [
                  _createElementVNode("span", _hoisted_3, [
                    _createVNode(Icon, { icon: "Preview" })
                  ]),
                  _createElementVNode("span", _hoisted_4, _toDisplayString(_unref($t)("menubar.view")) + "... ", 1)
                ])
              ]),
              _: 1
            })), [
              [
                _directive_b_modal,
                void 0,
                void 0,
                { "view-options-modal": true }
              ]
            ])
          ]),
          _: 1
        })
      ]),
      _: 1
    }),
    _createVNode(ViewOptionsModal),
    (_unref(currentTranslationProject))
      ? (_openBlock(), _createBlock(RenameTranslationProjectModal, {
          key: 0,
          modelValue: _unref(currentTranslationProject).name,
          "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((_unref(currentTranslationProject).name) = $event)),
          show: vm.showRenameModal,
          "onUpdate:show": _cache[3] || (_cache[3] = ($event: any) => ((vm.showRenameModal) = $event)),
          "translation-projects": _unref(translationProjects),
          "translation-project-id": _unref(currentTranslationProject).translationProjectId,
          title: _unref($t)('translationManager.renameDialog.title'),
          "ok-button-text": _unref($t)('OK'),
          "max-characters-name": 128,
          onConfirm: confirmRename,
          onCancel: cancelRename
        }, null, 8, ["modelValue", "show", "translation-projects", "translation-project-id", "title", "ok-button-text"]))
      : _createCommentVNode("", true),
    _createVNode(CreateTranslationProjectModal, {
      "translation-projects-names": translationProjectNames.value,
      show: vm.showCreateModal,
      "onUpdate:show": _cache[4] || (_cache[4] = ($event: any) => ((vm.showCreateModal) = $event)),
      onConfirm: confirmCreateTranslationProject
    }, null, 8, ["translation-projects-names", "show"]),
    _createVNode(OpenTranslationProjectModal, {
      "translation-projects": _unref(translationProjects),
      show: vm.showOpenModal,
      "onUpdate:show": _cache[5] || (_cache[5] = ($event: any) => ((vm.showOpenModal) = $event)),
      onConfirm: switchTranslationProject
    }, null, 8, ["translation-projects", "show"]),
    _createVNode(ProgressModal, {
      progress: vm.progressHandler.progress,
      onCancel: publishCancelled,
      "min-open-time": 1000,
      "auto-close": true
    }, null, 8, ["progress"])
  ])), [
    [
      _directive_shortcut,
      vm.shortcuts,
      void 0,
      {
        multiple: true,
        prevent: true
      }
    ]
  ])
}
}

})